import React, { useMemo, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AltRouteRounded as RouteIcon,
  CheckCircleRounded as SuccessIcon,
  LocationOnRounded as PointIcon,
  ReplayRounded as RtoIcon,
  ScheduleOutlined as TimelineIcon,
  ScheduleRounded as TimeIcon,
} from '@mui/icons-material';
import FloatingWorkspaceCard from '../workspace/FloatingWorkspaceCard';
import type { ViewportOcclusionRect } from '../../models/types';
import { isExecutionException } from './executionModel';
import type { ExecutionActivityViewModel, ExecutionTripViewModel } from './executionTypes';

interface ExecutionTripDetailsCardProps {
  trip: ExecutionTripViewModel;
  drawerWidth: number;
  onClose: () => void;
  onFocusActivity: (activity: ExecutionActivityViewModel) => void;
  activeActivityId?: string | null;
  hoveredActivityId?: string | null;
  onHoverActivity?: (activityId: string | null) => void;
  onOpenTimeline?: () => void;
  onOcclusionRectChange?: (rect: ViewportOcclusionRect | null) => void;
  bottomSheet?: boolean;
}

const formatTime = (epoch: number | null, timezone: string): string => {
  if (!epoch) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(epoch));
};

const formatDelta = (minutes: number | null): string => {
  if (minutes == null) return '—';
  if (minutes === 0) return 'On time';
  return `${minutes > 0 ? '+' : ''}${minutes}m`;
};

const TravelConnector: React.FC<{ activity: ExecutionActivityViewModel }> = ({ activity }) => {
  const { incomingTravel } = activity;
  if (incomingTravel.plannedDistanceKm == null && incomingTravel.actualDistanceKm == null) return null;
  return (
    <Stack direction="row" spacing={0.65} alignItems="center" sx={{ pl: 2.1, minHeight: 34, color: 'text.secondary' }}>
      <Box sx={{ width: 1, alignSelf: 'stretch', borderLeft: '2px dotted', borderColor: 'divider' }} />
      <RouteIcon sx={{ fontSize: 14 }} />
      <Typography variant="caption">
        Planned {incomingTravel.plannedDistanceKm == null ? '—' : `${incomingTravel.plannedDistanceKm.toFixed(1)} km`} · {incomingTravel.plannedMinutes == null ? '—' : `${incomingTravel.plannedMinutes}m`}
      </Typography>
      <Typography variant="caption" fontWeight={750} sx={{ color: '#0f766e' }}>
        Actual {incomingTravel.actualDistanceKm == null ? '—' : `${incomingTravel.actualDistanceKm.toFixed(1)} km`} · {incomingTravel.actualMinutes == null ? '—' : `${incomingTravel.actualMinutes}m`}
      </Typography>
    </Stack>
  );
};

const ActivityRow: React.FC<{
  activity: ExecutionActivityViewModel;
  timezone: string;
  color: string;
  onClick: () => void;
  highlighted?: boolean;
  onHover?: (activityId: string | null) => void;
}> = ({ activity, timezone, color, onClick, highlighted = false, onHover }) => {
  const theme = useTheme();
  const actual = activity.actualSequence != null;
  const outcomeIcon = activity.outcome === 'RTO'
    ? <RtoIcon sx={{ fontSize: 17, color: 'error.main' }} />
    : activity.outcome === 'SUCCESS' || activity.outcome === 'RETURNED'
      ? <SuccessIcon sx={{ fontSize: 17, color: 'success.main' }} />
      : <PointIcon sx={{ fontSize: 17, color: actual ? color : 'text.disabled' }} />;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      onMouseEnter={() => onHover?.(activity.id)}
      onMouseLeave={() => onHover?.(null)}
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: highlighted ? color : actual ? alpha(color, 0.24) : 'divider',
        bgcolor: highlighted ? alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.1) : actual ? alpha(color, theme.palette.mode === 'dark' ? 0.08 : 0.035) : 'background.paper',
        borderRadius: 2,
        p: 1,
        textAlign: 'left',
        cursor: 'pointer',
        color: 'text.primary',
        '&:hover': { borderColor: alpha(color, 0.52), bgcolor: alpha(color, 0.07) },
      }}
    >
      <Stack direction="row" spacing={0.8} alignItems="flex-start">
        <Box sx={{ pt: 0.1 }}>{outcomeIcon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.55} alignItems="center" useFlexGap flexWrap="wrap">
            {activity.actualSequence != null ? <Chip size="small" label={`Actual #${activity.actualSequence}`} sx={{ height: 19, fontSize: 9.5, bgcolor: alpha(color, 0.12), color }} /> : null}
            {activity.plannedSequence != null ? <Chip size="small" variant="outlined" label={`Planned #${activity.plannedSequence}`} sx={{ height: 19, fontSize: 9.5 }} /> : <Chip size="small" color="warning" label="Unplanned" sx={{ height: 19, fontSize: 9.5 }} />}
            <Typography variant="subtitle2" fontWeight={780} noWrap>{activity.title}</Typography>
          </Stack>
          {activity.subtitle ? <Typography variant="caption" color="text.secondary" display="block" noWrap>{activity.subtitle}</Typography> : null}

          <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 0.7 }}>
            <Box><Typography sx={{ fontSize: 9, color: 'text.secondary' }}>PLANNED ATTEMPT</Typography><Typography variant="caption" fontWeight={700}>{formatTime(activity.plannedAttemptAtEpoch, timezone)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 9, color: 'text.secondary' }}>ARRIVE / DEPART</Typography><Typography variant="caption" fontWeight={700}>{formatTime(activity.plannedArrivalAtEpoch, timezone)} / {formatTime(activity.plannedDepartureAtEpoch, timezone)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 9, color: '#0f766e' }}>ACTUAL IN / OUT</Typography><Typography variant="caption" fontWeight={750} sx={{ color: '#0f766e' }}>{formatTime(activity.actualCheckInAtEpoch, timezone)} / {formatTime(activity.actualCheckOutAtEpoch, timezone)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 9, color: 'text.secondary' }}>SERVICE / DELTA</Typography><Typography variant="caption" fontWeight={700}>{activity.serviceMinutes == null ? '—' : `${activity.serviceMinutes}m`} / {formatDelta(activity.arrivalDeltaMinutes)}</Typography></Box>
          </Box>
          {activity.exceptionTags.length ? (
            <Stack direction="row" spacing={0.45} useFlexGap flexWrap="wrap" sx={{ mt: 0.7 }}>
              {activity.exceptionTags.map((tag) => <Chip key={tag} size="small" color={tag === 'RTO' ? 'error' : 'warning'} variant="outlined" label={tag} sx={{ height: 19, fontSize: 9.5 }} />)}
            </Stack>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
};

const ExecutionTripDetailsCard: React.FC<ExecutionTripDetailsCardProps> = ({
  trip,
  drawerWidth,
  onClose,
  onFocusActivity,
  activeActivityId,
  hoveredActivityId,
  onHoverActivity,
  onOpenTimeline,
  onOcclusionRectChange,
  bottomSheet = false,
}) => {
  const [filter, setFilter] = useState<'all' | 'exceptions'>('all');
  const activities = useMemo(
    () => filter === 'exceptions' ? trip.activities.filter(isExecutionException) : trip.activities,
    [filter, trip.activities]
  );
  const timezone = trip.plannedTrip.timezone;

  return (
    <FloatingWorkspaceCard
      key={trip.id}
      title={trip.id}
      subtitle={`${trip.plannedTrip.vehicle.number || 'Vehicle unassigned'} · ${trip.plannedTrip.driver.name || 'Driver unassigned'}`}
      defaultPosition={{ x: drawerWidth + 16, y: 16 }}
      defaultSize={{ width: 620, height: 650 }}
      minSize={{ width: 480, height: 320 }}
      maxSize={{ width: 820, height: 820 }}
      persistenceKey="trips-execution-details"
      accentColor={trip.color}
      onClose={onClose}
      onOcclusionRectChange={onOcclusionRectChange}
      layoutMode={bottomSheet ? 'bottom-sheet' : 'floating'}
      bottomSheetHeight={540}
      headerActions={(
        <Stack direction="row" spacing={0.4} alignItems="center">
          {onOpenTimeline ? <Tooltip title="Open timeline"><IconButton size="small" aria-label="Open comparison Timeline" onClick={onOpenTimeline}><TimelineIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip> : null}
        </Stack>
      )}
      contentSx={{ display: 'flex', flexDirection: 'column' }}
      testId="trip-details-card"
    >
      <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }} data-floating-no-drag="true">
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_, value) => value && setFilter(value)}
            aria-label="Trip Details filter"
            sx={{ '& .MuiToggleButton-root': { py: 0.35, px: 1, fontSize: 10.5, textTransform: 'none' } }}
          >
            <ToggleButton value="all">All points</ToggleButton>
            <ToggleButton value="exceptions">Exceptions</ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem />
          <TimeIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            Actual journey first · planned context retained
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.2 }} data-floating-no-drag="true">
        {activities.length ? activities.map((activity, index) => (
          <React.Fragment key={activity.id}>
            {index > 0 ? <TravelConnector activity={activity} /> : null}
            <ActivityRow
              activity={activity}
              timezone={timezone}
              color={trip.color}
              onClick={() => onFocusActivity(activity)}
              highlighted={activity.id === activeActivityId || activity.id === hoveredActivityId}
              onHover={onHoverActivity}
            />
          </React.Fragment>
        )) : (
          <Box sx={{ display: 'grid', placeItems: 'center', height: 180, color: 'text.secondary' }}>
            <Typography variant="body2">No execution exceptions for this Trip.</Typography>
          </Box>
        )}
      </Box>
    </FloatingWorkspaceCard>
  );
};

export default ExecutionTripDetailsCard;
